import { AfterViewInit, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable, of, Subscriber, Subscription } from 'rxjs';
import { SiteContext } from '../../../shared/contexts/site.context';
import { PhoneUiContext } from '../../../../shared/phone-buttons/contexts/phone-ui.context';
import {
  ClusterResult,
  DevicePoolResult,
  Phone,
  SiteResult,
  SiteSummary,
} from '../../../../shared/models/generated/ziroModels';
import { ZiroIcons, ZiroStackedIcons } from '../../../../shared/models/ziro-icons.enum';
import { ButtonStyles } from '../../../../button/button.component';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../../forms/fields/select/ziro-select.component';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import { SiteSummaryContext } from '../../../../shared/contexts/site-summary.context';
import { take } from 'rxjs/operators';
import { ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { TranslateService } from '@ngx-translate/core';
import { PhoneResource } from '../../../../shared/resources/phone.resource';
import { ToastService } from '../../../../shared/services/toast.service';
import { ToastTypes } from '../../../../shared/services/abstract/toast.service.abstract';
import { Nvp } from '../../../../shared/models/nvp';

interface PublicPhoneChangeSiteEntity {
  site: number;
}

@Component({
  selector: 'ziro-public-phone-change-site',
  templateUrl: './public-phone-change-site.component.html',
  styleUrls: ['../public-phone.component.scss', './public-phone-change-site.component.scss'],
  standalone: false,
})
export class PublicPhoneChangeSiteComponent
  extends ZiroFormAbstractDirective<PublicPhoneChangeSiteEntity>
  implements OnInit, AfterViewInit, OnDestroy
{
  protected _ziroFormStateService: ZiroFormStateService;
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _siteContext = inject(SiteContext);
  private _phoneUiContext = inject(PhoneUiContext);
  private _siteSummaryContext = inject(SiteSummaryContext);
  private _translateService = inject(TranslateService);
  private _phoneResource = inject(PhoneResource);
  private _toastService = inject(ToastService);

  ZiroStackedIcons = ZiroStackedIcons;
  ziroIcons = ZiroIcons;
  buttonStyles = ButtonStyles;
  phoneId: string;
  isValidCluster = false;
  formConfig = {
    fields: {
      site: {
        label: 'tkey;change_site.public_phone.search.label',
        dataAutomation: 'public-phone-change-site-form-site',
        componentConfig: new ZiroSelectConfig(),
        validation: [
          {
            validator: (val: number) => {
              const cluster = this._getClusterForSite(val);
              this.isValidCluster = !!cluster && cluster.id === this._initialPhoneCluster.id;
              if (!val || cluster.id !== this._initialPhoneCluster.id) {
                return ZiroFormsValidationState.INVALID;
              }

              return ZiroFormsValidationState.VALID;
            },
            message: () => this._getValidationMessage(),
          },
        ],
      },
    },
    options: {
      columnClasses: {
        label: 'col-12 font-light',
        input: 'col-12',
      },
    },
  };

  private _phone: Phone;
  private _initialPhoneSite: SiteResult;
  private _initialPhoneCluster: ClusterResult;
  private _siteSummary: SiteSummary;
  private _subscriptions = new Subscription();
  private _initTimeout: number;

  ngOnInit(): void {
    const sub = combineLatest([
      this._siteSummaryContext.state$,
      this._phoneUiContext.phoneState$.pipe(take(1)),
    ]).subscribe(([siteSummary, phone]) => {
      this._siteSummary = siteSummary;
      this._phone = phone;
      this._siteContext.setSiteForPublicPhone(this._siteSummary, this._phone);
      this.phoneId = this._phone.id;
    });
    this._subscriptions.add(sub);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();

    this._initTimeout = window.setTimeout(() => {
      this._siteContext.state$.pipe(take(1)).subscribe((site) => {
        this._initialPhoneSite = site;
        this._initialPhoneCluster = this._getClusterForSite(Number(site.id));
        this.entitySource.next({
          site: null,
        });
        this._setFormOptions();
      });
    });
  }

  ngOnDestroy(): void {
    window.clearTimeout(this._initTimeout);
    this._subscriptions.unsubscribe();
  }

  onCancelClicked(): void {
    this._router.navigate(['../'], { relativeTo: this._route });
  }

  protected submit(): Observable<void> {
    if (this.entity.site) {
      const newDevicePool = this._getSiteDeskphoneDevicePool(this.entity.site);
      const site = this._getSiteFromId(this.entity.site);

      const updatedPhone = {
        ...this._phone,
        devicePool: newDevicePool,
      };
      const auditTags = [{ name: 'change site', value: `${this._initialPhoneSite.name} > ${site.name}` }] as Nvp[];

      return new Observable((subscriber: Subscriber<void>) => {
        this._phoneResource
          .put(updatedPhone, this._initialPhoneCluster.cucmServerId.toString(), auditTags)
          .subscribe(() => {
            this._phoneUiContext.set(updatedPhone);
            this._siteContext.setSiteForPublicPhone(this._siteSummary, updatedPhone);
            this._toastService.push(
              ToastTypes.SUCCESS,
              this.ziroIcons.SITE,
              'tkey;change.site.toast.title',
              'tkey;change.site.toast.message',
              { site: site.name },
            );
            this._router.navigate(['../'], { relativeTo: this._route });
            subscriber.next();
            subscriber.complete();
          });
      });
    }

    return of(null);
  }

  private _getClusterForSite(siteId: number): ClusterResult {
    return this._siteSummary.clusters.find((clusterResult: ClusterResult) => {
      const site = clusterResult.sites.find((siteResult: SiteResult) => siteResult.id === siteId);

      if (site) {
        return clusterResult;
      }
    });
  }

  private _getSiteFromId(siteId: number): SiteResult {
    let sites: SiteResult[] = [];
    this._siteSummary.clusters.forEach((clusterResult: ClusterResult) => {
      sites = sites.concat(clusterResult.sites);
    });

    return sites.find((siteResult: SiteResult) => siteResult.id === siteId);
  }

  private _getSiteDeskphoneDevicePool(siteId: number): string {
    const site = this._getSiteFromId(siteId);
    let newDevicePool = '';
    if (site) {
      const devicePool = site.devicePools.find(
        (devicePoolResult: DevicePoolResult) => devicePoolResult.serviceName === 'Desk Phone',
      );
      if (devicePool) {
        newDevicePool = devicePool.devicePool;
      }
    }

    return newDevicePool;
  }

  private _setFormOptions(): void {
    const sites: ZiroSelectOption[] = [];

    this._siteSummary.clusters.forEach((cluster: ClusterResult) => {
      cluster.sites
        .filter((site: SiteResult) => {
          const deskphoneDevicePool = site.devicePools.find((devicePool: DevicePoolResult) => {
            if (devicePool.serviceName === 'Desk Phone') {
              return devicePool;
            }
          });

          return site.id !== Number(this._initialPhoneSite.id) && site.hasPermission && deskphoneDevicePool;
        })
        .forEach((site: SiteResult) => {
          sites.push({
            label: site.name,
            value: site.id,
            group: cluster.name,
          });
        });
    });

    const dialPlanGroupSelect = this.fieldComponents.find((field) => field.fieldId() === 'site');
    dialPlanGroupSelect.applyComponentConfig(
      new ZiroSelectConfig({
        options: sites,
        bindValue: 'value',
      }),
    );
  }

  private _getValidationMessage(): string {
    return this._translateService.instant('tkey;change_site.public_phone.form.site.error.text', {
      clusterName: this._initialPhoneCluster.name,
    });
  }
}
